import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/CaseStudies.css';
import ScrollProgressIndicator from '../components/ScrollProgressIndicator';
import BookingSection from '../components/BookingSection';
import SEOHead from '../components/SEOHead';
import { seoConfig } from '../utils/seoConfig';

const CaseStudies = () => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const videoRef = useRef(null);

  // Case studies data
  const caseStudies = [
    {
      id: 1,
      title: 'E-Commerce Revolution',
      client: 'TechMart Online',
      category: 'website',
      beforeImage: '/assets/images/website-dev.png',
      afterImage: '/assets/images/SEO-optimizing.jpg',
      beforeStats: {
        traffic: '2.5K/month',
        conversion: '0.8%',
        revenue: '₹45K/month',
        bounce: '78%'
      },
      afterStats: {
        traffic: '25K/month',
        conversion: '4.2%',
        revenue: '₹4.8L/month',
        bounce: '32%'
      },
      description: 'Transformed a struggling e-commerce platform into a high-converting digital powerhouse.',
      challenge: 'TechMart was facing declining sales with an outdated website design, poor user experience, and virtually no online visibility.',
      solution: 'We completely redesigned their website with modern UI/UX, implemented advanced SEO strategies, and optimized their conversion funnel.',
      results: [
        '900% increase in organic traffic',
        '425% boost in conversion rate',
        '967% growth in monthly revenue',
        '59% reduction in bounce rate',
        'Page load time improved by 73%'
      ],
      timeline: '3 months',
      technologies: ['React', 'Node.js', 'MongoDB', 'AWS', 'Google Analytics'],
      testimonial: {
        text: "QuantumFrame Studio didn't just redesign our website - they transformed our entire business. The results speak for themselves!",
        author: 'Rajesh Kumar, CEO TechMart'
      }
    },
    {
      id: 2,
      title: 'YouTube Channel Explosion',
      client: 'FitLife Guru',
      category: 'youtube',
      beforeImage: '/assets/images/content-creation.jpg',
      afterImage: '/assets/images/video-production.jpg',
      beforeStats: {
        subscribers: '1.2K',
        views: '15K/month',
        engagement: '2.1%',
        revenue: '₹8K/month'
      },
      afterStats: {
        subscribers: '125K',
        views: '2.8M/month',
        engagement: '8.7%',
        revenue: '₹2.5L/month'
      },
      description: 'Skyrocketed a fitness YouTube channel from obscurity to viral success.',
      challenge: 'FitLife Guru had great content but poor video quality, inconsistent branding, and minimal audience engagement.',
      solution: 'We revamped their video production quality, created a consistent brand identity, optimized content strategy, and implemented viral marketing techniques.',
      results: [
        '10,317% increase in subscribers',
        '18,567% growth in monthly views',
        '314% improvement in engagement rate',
        '3,025% boost in monthly revenue',
        'Featured in YouTube trending 12 times'
      ],
      timeline: '6 months',
      technologies: ['Adobe Premiere Pro', 'After Effects', 'Photoshop', 'YouTube Analytics', 'TubeBuddy'],
      testimonial: {
        text: "From 1K to 125K subscribers in 6 months! QuantumFrame Studio made my YouTube dreams come true. Their strategy is pure gold!",
        author: 'Priya Sharma, FitLife Guru'
      }
    },
    {
      id: 3,
      title: 'Digital Marketing Mastery',
      client: 'Local Restaurant Chain',
      category: 'marketing',
      beforeImage: '/assets/images/facebook-marketing.png',
      afterImage: '/assets/images/Socialmedia-editing.jpg',
      beforeStats: {
        reach: '5K/month',
        leads: '12/month',
        sales: '₹1.2L/month',
        roi: '120%'
      },
      afterStats: {
        reach: '180K/month',
        leads: '450/month',
        sales: '₹8.5L/month',
        roi: '680%'
      },
      description: 'Transformed a local restaurant chain into a regional digital marketing success story.',
      challenge: 'The restaurant had minimal online presence, relied heavily on word-of-mouth, and struggled with customer acquisition costs.',
      solution: 'We developed a comprehensive digital marketing strategy including social media management, targeted advertising, content creation, and local SEO optimization.',
      results: [
        '3,500% increase in social media reach',
        '3,650% growth in qualified leads',
        '608% boost in monthly sales',
        '467% improvement in marketing ROI',
        'Expanded to 3 new locations'
      ],
      timeline: '4 months',
      technologies: ['Facebook Ads', 'Google Ads', 'Instagram', 'WhatsApp Business', 'Google My Business'],
      testimonial: {
        text: "QuantumFrame Studio's digital marketing strategy helped us expand from 1 to 4 locations in just 4 months. Incredible results!",
        author: 'Amit Patel, Restaurant Owner'
      }
    }
  ];

  // Key insights data
  const insights = [
    {
      icon: '🎨',
      title: 'Good Design = More Traffic',
      description: 'Well-designed websites receive 94% more views than poorly designed ones. Professional aesthetics build trust and encourage exploration.',
      stat: '94%',
      color: '#ff6b35'
    },
    {
      icon: '⚡',
      title: 'Speed Matters',
      description: 'A 1-second delay in page load time can reduce conversions by 7%. Fast websites rank higher and convert better.',
      stat: '7%',
      color: '#00ff41'
    },
    {
      icon: '📱',
      title: 'Mobile-First Approach',
      description: 'Over 60% of web traffic comes from mobile devices. Responsive design is no longer optional - it\'s essential.',
      stat: '60%',
      color: '#14eaf5'
    },
    {
      icon: '🔍',
      title: 'SEO Drives Growth',
      description: 'Organic search drives 53% of all website traffic. Proper SEO optimization is the foundation of digital success.',
      stat: '53%',
      color: '#ffd23f'
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Video autoplay setup
    if (videoRef.current) {
      const video = videoRef.current;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;
      video.autoplay = true;
      
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Case studies video autoplay started');
          })
          .catch((error) => {
            console.log('Case studies video autoplay prevented:', error);
          });
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCase) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [selectedCase]);

  const filteredCases = activeTab === 'all' 
    ? caseStudies 
    : caseStudies.filter(caseStudy => caseStudy.category === activeTab);

  const handleCaseClick = (caseStudy) => {
    setSelectedCase(caseStudy);
  };

  const handleClose = () => {
    setSelectedCase(null);
  };

  const StatComparison = ({ before, after, label }) => (
    <div className="cs-stat-comparison">
      <div className="cs-stat-label">{label}</div>
      <div className="cs-stat-values">
        <div className="cs-stat-before">
          <span className="cs-stat-value">{before}</span>
          <span className="cs-stat-period">Before</span>
        </div>
        <div className="cs-stat-arrow">→</div>
        <div className="cs-stat-after">
          <span className="cs-stat-value">{after}</span>
          <span className="cs-stat-period">After</span>
        </div>
      </div>
    </div>
  );

  const CaseCard = ({ caseStudy }) => (
    <motion.div
      className="cs-case-card"
      onClick={() => handleCaseClick(caseStudy)}
      whileHover={{ scale: 1.02, y: -5 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="cs-card-header">
        <div className="cs-card-category">{caseStudy.category}</div>
        <div className="cs-card-timeline">{caseStudy.timeline}</div>
      </div>
      
      <div className="cs-card-images">
        <div className="cs-image-container cs-before">
          <img src={caseStudy.beforeImage} alt="Before" />
          <div className="cs-image-label">Before</div>
        </div>
        <div className="cs-vs-divider">VS</div>
        <div className="cs-image-container cs-after">
          <img src={caseStudy.afterImage} alt="After" />
          <div className="cs-image-label">After</div>
        </div>
      </div>

      <div className="cs-card-content">
        <h3>{caseStudy.title}</h3>
        <p className="cs-client">{caseStudy.client}</p>
        <p className="cs-description">{caseStudy.description}</p>
        
        <div className="cs-quick-stats">
          {Object.entries(caseStudy.afterStats).slice(0, 2).map(([key, value], index) => (
            <div key={key} className="cs-quick-stat">
              <span className="cs-stat-value">{value}</span>
              <span className="cs-stat-label">{key}</span>
            </div>
          ))}
        </div>
        
        <div className="cs-card-footer">
          <span className="cs-learn-more">View Full Case Study →</span>
        </div>
      </div>
    </motion.div>
  );

  const ExpandedCase = ({ caseStudy, onClose }) => (
    <motion.div
      className="cs-expanded-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="cs-expanded-case"
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
      >
        <button className="cs-close-button" onClick={onClose}>×</button>
        
        <div className="cs-expanded-content">
          <div className="cs-expanded-header">
            <h2>{caseStudy.title}</h2>
            <p className="cs-expanded-client">{caseStudy.client}</p>
            <div className="cs-expanded-meta">
              <span className="cs-category">{caseStudy.category}</span>
              <span className="cs-timeline">{caseStudy.timeline}</span>
            </div>
          </div>

          <div className="cs-expanded-body">
            <div className="cs-section">
              <h3>The Challenge</h3>
              <p>{caseStudy.challenge}</p>
            </div>

            <div className="cs-section">
              <h3>Our Solution</h3>
              <p>{caseStudy.solution}</p>
            </div>

            <div className="cs-section">
              <h3>Results Achieved</h3>
              <div className="cs-results-grid">
                {caseStudy.results.map((result, index) => (
                  <motion.div
                    key={index}
                    className="cs-result-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <span className="cs-result-icon">✓</span>
                    <span className="cs-result-text">{result}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="cs-section">
              <h3>Performance Metrics</h3>
              <div className="cs-stats-grid">
                {Object.entries(caseStudy.beforeStats).map(([key, beforeValue]) => (
                  <StatComparison
                    key={key}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    before={beforeValue}
                    after={caseStudy.afterStats[key]}
                  />
                ))}
              </div>
            </div>

            <div className="cs-section">
              <h3>Technologies Used</h3>
              <div className="cs-tech-stack">
                {caseStudy.technologies.map((tech, index) => (
                  <span key={index} className="cs-tech-tag">{tech}</span>
                ))}
              </div>
            </div>

            <div className="cs-section cs-testimonial">
              <h3>Client Testimonial</h3>
              <blockquote>
                <p>"{caseStudy.testimonial.text}"</p>
                <cite>- {caseStudy.testimonial.author}</cite>
              </blockquote>
            </div>
          </div>

          <div className="cs-expanded-footer">
            <Link to="/contact">
              <motion.button
                className="cs-contact-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Success Story
              </motion.button>
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      <SEOHead 
        title={seoConfig.pages.caseStudies.title}
        description={seoConfig.pages.caseStudies.description}
        keywords={seoConfig.pages.caseStudies.keywords}
        type={seoConfig.pages.caseStudies.type}
        image={seoConfig.pages.caseStudies.image}
        schema={seoConfig.pages.caseStudies.schema}
        publishedTime={seoConfig.pages.caseStudies.publishedTime}
        modifiedTime={seoConfig.pages.caseStudies.modifiedTime}
      />
      
      {/* Under Construction Overlay */}
      <div className="cnst-under-construction">
        <div className="cnst-construction-content">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="cnst-construction-card"
          >
            <div className="cnst-back-button-top-inside">
              <Link to="/" className="cnst-back-button-inside">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="cnst-back-btn-inside"
                >
                  ← Back to Homepage
                </motion.button>
              </Link>
            </div>
            <div className="cnst-construction-icon">
              🚧
            </div>
            <h1>Under Construction</h1>
            <p>We're working hard to bring you amazing case studies showcasing our success stories and client transformations.</p>
            <div className="cnst-construction-features">
              <div className="cnst-feature-item">
                <span className="cnst-feature-icon">📈</span>
                <span>900% Traffic Growth Stories</span>
              </div>
              <div className="cnst-feature-item">
                <span className="cnst-feature-icon">🚀</span>
                <span>Viral YouTube Success Cases</span>
              </div>
              <div className="cnst-feature-item">
                <span className="cnst-feature-icon">💼</span>
                <span>Business Transformation Results</span>
              </div>
            </div>
            <p className="cnst-construction-note">
              Check back soon to see how we've helped businesses achieve incredible results!
            </p>
            <div className="cnst-construction-buttons">
              <Link to="/contact" className="cnst-construction-cta">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="cnst-contact-us-btn"
                >
                  Contact Us for Your Success Story
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
      
      <video 
        ref={videoRef}
        autoPlay 
        muted 
        loop 
        playsInline
        webkit-playsinline="true"
        preload="auto"
        className="cs-background-video"
        style={{ display: 'none' }}
      >
        <source src="/assets/video/full-backgroundvid.mp4" type="video/mp4" />
      </video>

      <ScrollProgressIndicator />

      <div className="cs-page" style={{ display: 'none' }}>
        {/* Hero Section */}
        <section className="cs-hero">
          <motion.div
            className="cs-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Success Stories</h1>
            <p>Discover how we've transformed businesses and skyrocketed their digital presence. From struggling websites to viral YouTube channels, see the incredible results we deliver.</p>
          </motion.div>
        </section>

        {/* Key Insights Section */}
        <section className="cs-insights">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Why Professional Design Matters
          </motion.h2>
          <div className="cs-insights-grid">
            {insights.map((insight, index) => (
              <motion.div
                key={index}
                className="cs-insight-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                style={{ borderColor: insight.color }}
              >
                <div className="cs-insight-icon" style={{ color: insight.color }}>
                  {insight.icon}
                </div>
                <div className="cs-insight-stat" style={{ color: insight.color }}>
                  {insight.stat}
                </div>
                <h3>{insight.title}</h3>
                <p>{insight.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Filter Tabs */}
        <section className="cs-filter-section">
          <div className="cs-filter-tabs">
            {['all', 'website', 'youtube', 'marketing'].map((tab) => (
              <button
                key={tab}
                className={`cs-filter-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'all' && ' Cases'}
                {tab === 'website' && ' Design'}
                {tab === 'youtube' && ' Growth'}
                {tab === 'marketing' && ' Campaigns'}
              </button>
            ))}
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="cs-cases-section">
          <div className="cs-cases-grid">
            <AnimatePresence mode="wait">
              {filteredCases.map((caseStudy) => (
                <CaseCard key={caseStudy.id} caseStudy={caseStudy} />
              ))}
            </AnimatePresence>
          </div>
        </section>

        {/* Call to Action */}
        <section className="cs-cta">
          <motion.div
            className="cs-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Be Our Next Success Story?</h2>
            <p>Join the ranks of businesses that have transformed their digital presence with QuantumFrame Studio. Let's create your success story together.</p>
            <Link to="/contact">
              <motion.button
                className="cs-cta-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Your Transformation
              </motion.button>
            </Link>
          </motion.div>
        </section>

        <BookingSection />

        {/* Expanded Case Modal */}
        <AnimatePresence>
          {selectedCase && (
            <ExpandedCase caseStudy={selectedCase} onClose={handleClose} />
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default CaseStudies;
